import { gql, useMutation, useQuery } from '@apollo/client';
import { Dialog, Button } from 'antd-mobile';
import {useLocation, useNavigate} from 'react-router-dom';
import { Pda } from '../components/Pda';
import { Header } from '../components/Header';
import { HandleScanEventResult, VehicleRelatedScanComponent } from '../components/VehicleRelatedScanComponent';
import { t } from '../../../i18n';
import { parseException, parseExceptionMessageKey } from '../../../utils/exceptionUtils';
import { getBottomNavBarContainerStyle } from '../../../utils/deviceUtils';
import {TmsPageState} from "../types/tms";
import { WaypointType, TmsOperationSourceEnum } from '../../../enums/TmsEnum';
import {PdaPath} from "../../../enums/PdaNavigatePathEnum";
import { TmsWaypointStepIndicator } from './components/TmsWaypointStepIndicator';
import {useEffect, useRef, useState} from "react";
import {
    GET_NEEDS_TMS_UNLOADING_OPERATION,
    NeedsTmsUnloadingOperationResponse
} from '../../../graphql/queries/tmsTaskWarehouseOperationQueries';

const HANDLE_TMS_WHS_SEALED_SCAN_EVENT = gql`
mutation TmsWarehouseOperationsMutation($input: HandleTmsWhsSealedScanEventInput!) {
  handleTmsWhsSealedScanEvent(input: $input) {
    success
    message
    __typename
  }
}
`

interface HandleTmsWhsSealedScanEventInput {
    taskWayPointId: number;
    sealNumber: string;
    taskId: number;
    forceSubmit: boolean;
}

interface HandleTmsWhsSealedScanEventResponse {
    handleTmsWhsSealedScanEvent: {
        success: boolean;
        message: string;
        __typename: string;
    };
}

interface PageBodyProps {
    onScanSuccess: () => void;
}

const PageBody: React.FC<PageBodyProps> = ({ onScanSuccess }) => {

    const [handleTmsWhsSealedScanEvent] = useMutation<HandleTmsWhsSealedScanEventResponse>(HANDLE_TMS_WHS_SEALED_SCAN_EVENT);
    const location = useLocation();
    const { backRoute, ...taskData } = location.state as TmsPageState;
    const handleScanEvent = async (value1: string, value2: string, forceSubmit: boolean = false): Promise<HandleScanEventResult> => {
        if (value2 === "") {
            return {success: false, message: t('pda.common.sealNumberRequired') || '封车号必填'};
        }

        try {
            const {data} = await handleTmsWhsSealedScanEvent({
                variables: {
                    input: {
                        taskWayPointId: taskData.taskWaypointId,
                        sealNumber: value2,
                        taskId: taskData.taskId,
                        forceSubmit: forceSubmit,
                        operationSource: TmsOperationSourceEnum.PDA,
                    },
                },
            });

            const success = data?.handleTmsWhsSealedScanEvent?.success || false;
            const responseMessage = data?.handleTmsWhsSealedScanEvent?.message || '';

            // 如果接口返回失败，返回失败原因
            if (!success) {
                return {success: false, message: responseMessage || t('pda.common.scanFailed') || '扫描失败'};
            }

            // 接口返回成功时，通知上层可以启用“完成”按钮
            onScanSuccess();
            return {success: true};
        } catch (error) {
            // 解析错误信息
            const { message } = parseException(error);
            const messageKey = parseExceptionMessageKey(error);

            // 特殊处理：当 messageKey 为 'errors.business.tms.taskWaypoint.notLoadedWithOptions' 时，显示继续和关闭选项
            if (messageKey === 'errors.business.tms.taskWaypoint.notLoadedWithOptions') {
                return new Promise((resolve) => {
                    Dialog.confirm({
                        content: (
                            <div style={{ backgroundColor: '#ffffff', padding: '16px' }}>
                                <div className="text-base text-center text-gray-800">
                                    {message}
                                </div>
                            </div>
                        ),
                        confirmText: t('pda.common.continueButton'),
                        cancelText: t('pda.common.cancel'),
                        onConfirm: async () => {
                            // 点击继续后，重新发起接口请求，forceSubmit 设为 true
                            try {
                                const {data} = await handleTmsWhsSealedScanEvent({
                                    variables: {
                                        input: {
                                            taskWayPointId: taskData.taskWaypointId,
                                            sealNumber: value2,
                                            taskId: taskData.taskId,
                                            forceSubmit: true,
                                            operationSource: TmsOperationSourceEnum.PDA,
                                        },
                                    },
                                });
                                // 接口调用成功，返回成功结果
                                const retrySuccess = data?.handleTmsWhsSealedScanEvent?.success || false;
                                const retryMessage = data?.handleTmsWhsSealedScanEvent?.message || '';

                                if (!retrySuccess) {
                                    resolve({ success: false, message: retryMessage || t('pda.common.scanFailed') || '扫描失败' });
                                } else {
                                    // 重试成功时，通知上层可以启用“完成”按钮
                                    onScanSuccess();
                                    resolve({ success: true });
                                }
                            } catch (retryError) {
                                // 重试失败，显示错误提示
                                const { message: retryMessage } = parseException(retryError);
                                await Dialog.alert({
                                    content: (
                                        <div style={{backgroundColor: '#ffffff', padding: '16px'}}>
                                            <div className="text-base text-center text-gray-800">
                                                {retryMessage}
                                            </div>
                                        </div>
                                    ),
                                    confirmText: t('pda.common.cancel'),
                                });
                                resolve({ success: false, message: retryMessage });
                            }
                        },
                        onCancel: () => {
                            // 点击取消，返回失败结果，避免卡住后续扫描
                            resolve({ success: false, message: t('errors.business.tms.taskWaypoint.notLoaded') });
                        },
                    });
                });
            }

            // 其他错误，显示带确定按钮的对话框，背景为白色
            await Dialog.alert({
                content: (
                    <div style={{backgroundColor: '#ffffff', padding: '16px'}}>
                        <div className="text-base text-center text-gray-800">
                            {message}
                        </div>
                    </div>
                ),
                confirmText: t('pda.common.cancel'),
            });

            // 不再向上传递异常，只返回失败结果，避免重复错误提示
            return { success: false, message: message };
        }
    };

    return (
        <div className="flex flex-col gap-2">
            {/* 步骤节点展示 */}
            <TmsWaypointStepIndicator currentStep={3} />

            <VehicleRelatedScanComponent
                value1Placeholder={t('pda.pages.sealed.licensePlate')}
                value2Placeholder={t('pda.pages.sealed.sealNO.')}
                value1ColumnTitle={t('pda.pages.sealed.licensePlate')}
                value2ColumnTitle={t('pda.pages.sealed.sealNO.')}
                isCheckBarCodeValid={false}
                handleScanEvent={handleScanEvent}
                hideValue1Input
                isValue1Required={false}
            />
        </div>
    );
}

export default function TmsSealingPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { backRoute, ...taskData } = location.state as TmsPageState;
    const buttonContainerRef = useRef<HTMLDivElement>(null);
    const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastViewportHeightRef = useRef<number>(window.visualViewport?.height || window.innerHeight);
    const [canComplete, setCanComplete] = useState(false);

    const taskId = taskData?.taskId ? parseInt(taskData.taskId) : NaN;
    const taskWaypointId = taskData?.taskWaypointId ? parseInt(taskData.taskWaypointId) : NaN;
    const hasUnLoadingTime = taskData.task?.hasUnLoadingTime === true;
    const isOriginStation = taskData?.waypointType === WaypointType.ORIGIN;
    const shouldCheckUnloading =
        !isNaN(taskId) && !isNaN(taskWaypointId) && !hasUnLoadingTime && !isOriginStation;

    const { data: unloadingCheckData } = useQuery<NeedsTmsUnloadingOperationResponse>(
        GET_NEEDS_TMS_UNLOADING_OPERATION,
        {
            variables: { taskId, taskWayPointId: taskWaypointId },
            skip: !shouldCheckUnloading
        }
    );

    useEffect(() => {
        if (!shouldCheckUnloading) return;
        if (unloadingCheckData?.needsTmsUnloadingOperation === true) {
            Dialog.alert({
                content: t('pda.pages.sealed.unloadingRequired'),
                confirmText: t('pda.common.confirm') || '确认',
                onConfirm: () => {
                    if (backRoute) navigate(backRoute);
                    else navigate(-1);
                },
            });
        }
    }, [shouldCheckUnloading, unloadingCheckData, backRoute, navigate]);

    // 监听键盘显示/隐藏 - 直接操作 DOM，不触发 React 重新渲染
    useEffect(() => {
        const handleFocusIn = (e: FocusEvent) => {
            const target = e.target as HTMLElement;
            // 只在非按钮元素获得焦点时隐藏按钮
            if (target && !target.closest('button') && buttonContainerRef.current) {
                // 清除之前的超时
                if (focusTimeoutRef.current) {
                    clearTimeout(focusTimeoutRef.current);
                }
                // 延迟一小段时间，确保焦点真正停留在输入框上（避免快速切换导致的问题）
                focusTimeoutRef.current = setTimeout(() => {
                    if (buttonContainerRef.current) {
                        buttonContainerRef.current.style.opacity = '0';
                        buttonContainerRef.current.style.pointerEvents = 'none';
                    }
                }, 50);
            }
        };

        const handleFocusOut = () => {
            // 清除之前的超时
            if (focusTimeoutRef.current) {
                clearTimeout(focusTimeoutRef.current);
            }
            if (buttonContainerRef.current) {
                // 直接显示按钮
                buttonContainerRef.current.style.opacity = '1';
                buttonContainerRef.current.style.pointerEvents = 'auto';
            }
        };

        // 监听 visualViewport 变化，检测虚拟键盘的显示/隐藏
        const handleViewportChange = () => {
            const currentHeight = window.visualViewport?.height || window.innerHeight;
            const lastHeight = lastViewportHeightRef.current;

            // 如果高度增加了，说明键盘消失了
            if (currentHeight > lastHeight && buttonContainerRef.current) {
                // 确保按钮显示
                buttonContainerRef.current.style.opacity = '1';
                buttonContainerRef.current.style.pointerEvents = 'auto';
            }
            // 如果高度减少了，说明键盘显示了，检查焦点
            else if (currentHeight < lastHeight) {
                const activeElement = document.activeElement as HTMLElement;
                if (activeElement && !activeElement.closest('button') && buttonContainerRef.current) {
                    buttonContainerRef.current.style.opacity = '0';
                    buttonContainerRef.current.style.pointerEvents = 'none';
                }
            }

            lastViewportHeightRef.current = currentHeight;
        };

        window.addEventListener('focusin', handleFocusIn);
        window.addEventListener('focusout', handleFocusOut);
        window.visualViewport?.addEventListener('resize', handleViewportChange);

        return () => {
            window.removeEventListener('focusin', handleFocusIn);
            window.removeEventListener('focusout', handleFocusOut);
            window.visualViewport?.removeEventListener('resize', handleViewportChange);
            if (focusTimeoutRef.current) {
                clearTimeout(focusTimeoutRef.current);
            }
        };
    }, []);

    if (!taskData) {
        return null;
    }
    return (
        <div className="w-full h-full flex flex-col">
            <div className="flex flex-col gap-2 p-2 flex-1 overflow-y-auto pb-20">
                <Header title={t('pda.pages.shipOut.sealing')} backRoute={backRoute} />
                <Pda />
                <PageBody onScanSuccess={() => setCanComplete(true)} />
            </div>

            {/* 固定在底部的按钮 - 使用sticky替代fixed防止被虚拟键盘顶起 */}
            <div
                ref={buttonContainerRef}
                className={`${getBottomNavBarContainerStyle('fixed bottom-0 left-0 w-full p-2 bg-white border-t border-gray-200 z-20').className} transition-opacity duration-300`}
                style={{
                    ...getBottomNavBarContainerStyle('fixed bottom-0 left-0 w-full p-2 bg-white border-t border-gray-200 z-20').style,
                    willChange: 'opacity'
                }}>
                <div className="flex gap-2">
                    <Button
                        block
                        color="default"
                        disabled={canComplete}
                        onClick={() => navigate(PdaPath.PDA_TMS_LOADING_DETAIL, { state: { backRoute, ...taskData } })}
                        style={{ flex: 1 }}
                    >
                        {t('pda.common.previousStep')}
                    </Button>
                    <Button
                        block
                        color="primary"
                        disabled={!canComplete}
                        onClick={() => navigate(backRoute|| "/pda/home",{ state: { backRoute, ...taskData } })}
                        style={{ flex: 1 }}
                    >
                        {t('pda.pages.tms.arrival.complete') || t('pda.common.complete') || '完成'}
                    </Button>
                </div>
            </div>
        </div>
    );
}